import { forwardRef } from 'react'

import { clsx } from 'clsx'
import { Command } from 'cmdk'

import { Badge, Flex, Text, Tooltip } from '@buffer-mono/popcorn'

import type { CommandBarAction } from './actions/types'

import styles from './CommandBar.module.css'

/**
 * Props for the CommandBarItem component.
 */
interface CommandBarItemProps {
  /** The action to render */
  action: CommandBarAction
  /** Callback when the action is selected */
  onSelect?: (action: CommandBarAction) => void
  /** Additional CSS class name */
  className?: string
}

export const CommandBarItem = forwardRef<HTMLDivElement, CommandBarItemProps>(
  ({ action, onSelect, className }, ref) => {
    const handleSelect = (): void => {
      onSelect?.(action)
    }

    const baseAriaLabel = action.description
      ? `${action.displayName}. ${action.description}`
      : action.displayName

    const unreadSuffix =
      action.unrepliedCommentsCount !== undefined &&
      action.unrepliedCommentsCount > 0
        ? `. ${action.unrepliedCommentsCount} unreplied ${
            action.unrepliedCommentsCount === 1 ? 'comment' : 'comments'
          }`
        : ''

    const ariaLabel = `${baseAriaLabel}${unreadSuffix}`

    return (
      <Command.Item
        ref={ref}
        value={action.id}
        aria-label={ariaLabel}
        onSelect={handleSelect}
        className={clsx(styles.item, className)}
      >
        <Flex align="center" gap="sm" className={styles.itemContent}>
          {action.icon && (
            <span className={styles.itemIcon}>{action.icon}</span>
          )}
          <Flex direction="column" className={styles.itemText}>
            <Text size="md" weight="medium">
              {action.displayName}
            </Text>
            {action.description && (
              <Text size="sm" color="subtle">
                {action.description}
              </Text>
            )}
          </Flex>
        </Flex>
        {action.unrepliedCommentsCount !== undefined &&
          action.unrepliedCommentsCount > 0 && (
            <Tooltip
              content={`${action.unrepliedCommentsCount} unreplied ${
                action.unrepliedCommentsCount === 1 ? 'comment' : 'comments'
              }`}
            >
              <span>
                <Badge size="small" className={styles.unreadBadge}>
                  {action.unrepliedCommentsCount >= 100
                    ? '99+'
                    : action.unrepliedCommentsCount}
                </Badge>
              </span>
            </Tooltip>
          )}
      </Command.Item>
    )
  },
)

CommandBarItem.displayName = 'CommandBarItem'
